import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { leadsService } from '../../services/leads.service'
import toast from 'react-hot-toast'

interface LeadDetailModalProps {
  leadId: number
  onClose: () => void
}

export default function LeadDetailModal({ leadId, onClose }: LeadDetailModalProps) {
  const queryClient = useQueryClient()
  const [editedText, setEditedText] = useState('')
  const [isEditing, setIsEditing] = useState(false)
  const [replyText, setReplyText] = useState('')
  const [showReplyPanel, setShowReplyPanel] = useState(false)

  // Fetch lead details
  const { data: lead, isLoading } = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => leadsService.getLead(leadId),
  })

  // Update message mutation
  const updateMessageMutation = useMutation({
    mutationFn: ({ messageId, text }: { messageId: number; text: string }) =>
      leadsService.updateMessage(messageId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      setIsEditing(false)
      toast.success('Сообщение обновлено')
    },
    onError: () => {
      toast.error('Ошибка при обновлении сообщения')
    },
  })

  // Send message mutation (async queue)
  const sendMessageMutation = useMutation({
    mutationFn: (messageId: number) => leadsService.sendMessages([messageId]),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      queryClient.invalidateQueries({ queryKey: ['leadStats'] })
      queryClient.invalidateQueries({ queryKey: ['sendQueue'] })
      toast.success(data.message)
    },
    onError: () => {
      toast.error('Ошибка при отправке сообщения')
    },
  })

  // Suggest reply mutation
  const suggestReplyMutation = useMutation({
    mutationFn: () => leadsService.suggestReply(leadId, { language: 'ru' }),
    onSuccess: (data) => {
      setReplyText(data.reply_text)
      setShowReplyPanel(true)
      toast.success(`Ответ сгенерирован (${data.tokens_used} токенов)`)
    },
    onError: () => {
      toast.error('Ошибка при генерации ответа')
    },
  })

  // Send reply mutation
  const sendReplyMutation = useMutation({
    mutationFn: () => leadsService.sendReply(leadId, { reply_text: replyText }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] })
      queryClient.invalidateQueries({ queryKey: ['leads'] })
      setShowReplyPanel(false)
      setReplyText('')
      toast.success('Ответ отправлен в тред')
    },
    onError: () => {
      toast.error('Ошибка при отправке ответа')
    },
  })

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('ru-RU')
  }

  const statusColors: Record<string, string> = {
    pending_review: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    message_generated: 'bg-blue-100 text-blue-800',
    sent: 'bg-purple-100 text-purple-800',
    replied: 'bg-emerald-100 text-emerald-800',
  }

  const statusLabels: Record<string, string> = {
    pending_review: 'На рассмотрении',
    approved: 'Одобрен',
    rejected: 'Отклонён',
    message_generated: 'Сообщение готово',
    sent: 'Отправлено',
    replied: 'Получен ответ',
  }

  const handleStartEdit = () => {
    if (lead?.message) {
      setEditedText(lead.message.edited_text || lead.message.generated_text)
      setIsEditing(true)
    }
  }

  const handleSaveEdit = () => {
    if (lead?.message) {
      updateMessageMutation.mutate({
        messageId: lead.message.id,
        text: editedText,
      })
    }
  }

  const handleSend = () => {
    if (lead?.message) {
      sendMessageMutation.mutate(lead.message.id)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-auto overflow-hidden">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : lead ? (
            <>
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    {lead.username ? `@${lead.username}` : `ID: ${lead.telegram_user_id}`}
                  </h3>
                  <span
                    className={`px-2 py-0.5 text-xs rounded-full ${
                      statusColors[lead.status] || 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {statusLabels[lead.status] || lead.status}
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
                {/* Lead Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Информация о лиде</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    <p className="text-sm">
                      <span className="text-gray-500">Канал:</span>{' '}
                      <span className="text-gray-900">{lead.channel_title || 'Неизвестно'}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-500">Дата:</span>{' '}
                      <span className="text-gray-900">{formatDate(lead.created_at)}</span>
                    </p>
                    {lead.confidence !== null && (
                      <p className="text-sm">
                        <span className="text-gray-500">Уверенность:</span>{' '}
                        <span className={`font-medium ${
                          lead.confidence >= 80
                            ? 'text-green-600'
                            : lead.confidence >= 50
                            ? 'text-yellow-600'
                            : 'text-red-600'
                        }`}>
                          {lead.confidence}%
                        </span>
                      </p>
                    )}
                    {lead.contacted_at && (
                      <p className="text-sm">
                        <span className="text-gray-500">Контакт:</span>{' '}
                        <span className="text-gray-900">{formatDate(lead.contacted_at)}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Original Message */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Оригинальное сообщение</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-gray-900 whitespace-pre-wrap">{lead.message_text}</p>
                  </div>
                </div>

                {/* Need Description */}
                {lead.need_description && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Описание потребности</h4>
                    <div className="bg-blue-50 rounded-lg p-4">
                      <p className="text-blue-900">{lead.need_description}</p>
                    </div>
                  </div>
                )}

                {/* Generated Message */}
                {lead.message && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-500">Сгенерированное сообщение</h4>
                      {lead.status === 'message_generated' && !isEditing && (
                        <button
                          onClick={handleStartEdit}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          Редактировать
                        </button>
                      )}
                    </div>
                    {isEditing ? (
                      <div className="space-y-2">
                        <textarea
                          value={editedText}
                          onChange={(e) => setEditedText(e.target.value)}
                          rows={6}
                          className="input w-full"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMessageMutation.isPending}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                          >
                            {updateMessageMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                          </button>
                          <button
                            onClick={() => setIsEditing(false)}
                            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-green-50 rounded-lg p-4">
                        <p className="text-green-900 whitespace-pre-wrap">
                          {lead.message.edited_text || lead.message.generated_text}
                        </p>
                      </div>
                    )}

                    {/* Send Status Badge */}
                    {lead.message.send_status && lead.message.send_status !== 'draft' && (
                      <div className="mt-2 flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                          lead.message.send_status === 'sent' ? 'bg-green-100 text-green-700' :
                          lead.message.send_status === 'queued' ? 'bg-yellow-100 text-yellow-700' :
                          lead.message.send_status === 'sending' ? 'bg-blue-100 text-blue-700' :
                          lead.message.send_status === 'failed' ? 'bg-red-100 text-red-700' :
                          lead.message.send_status === 'paused' ? 'bg-gray-100 text-gray-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {lead.message.send_status === 'sent' ? 'Отправлено' :
                           lead.message.send_status === 'queued' ? 'В очереди' :
                           lead.message.send_status === 'sending' ? 'Отправка...' :
                           lead.message.send_status === 'failed' ? 'Ошибка' :
                           lead.message.send_status === 'paused' ? 'На паузе' :
                           lead.message.send_status}
                        </span>
                        {lead.message.error_text && (
                          <span className="text-xs text-red-500">{lead.message.error_text}</span>
                        )}
                      </div>
                    )}
                    {lead.message.sent_at && (
                      <p className="text-sm text-gray-500 mt-2">
                        Отправлено: {formatDate(lead.message.sent_at)}
                      </p>
                    )}
                    {lead.message.read_at && (
                      <p className="text-sm text-gray-500">
                        Прочитано: {formatDate(lead.message.read_at)}
                      </p>
                    )}
                    {lead.message.replied && (
                      <p className="text-sm text-green-600 font-medium">
                        ✓ Получен ответ
                      </p>
                    )}
                  </div>
                )}

                {/* Reply Suggestion Panel */}
                {(lead.status === 'approved' || lead.status === 'pending_review') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-500">Ответ в тред</h4>
                      {!showReplyPanel && (
                        <button
                          onClick={() => suggestReplyMutation.mutate()}
                          disabled={suggestReplyMutation.isPending}
                          className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                        >
                          {suggestReplyMutation.isPending ? 'Генерация...' : 'Предложить ответ'}
                        </button>
                      )}
                    </div>
                    {showReplyPanel && (
                      <div className="space-y-2">
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                          rows={4}
                          className="input w-full"
                          placeholder="Текст ответа..."
                        />
                        <div className="flex items-center justify-between">
                          <span className={`text-xs ${replyText.length > 450 ? 'text-red-500' : 'text-gray-400'}`}>
                            {replyText.length}/500
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => { setShowReplyPanel(false); setReplyText('') }}
                              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                            >
                              Отмена
                            </button>
                            <button
                              onClick={() => suggestReplyMutation.mutate()}
                              disabled={suggestReplyMutation.isPending}
                              className="px-3 py-1.5 border border-blue-300 text-blue-600 rounded-lg text-sm hover:bg-blue-50 disabled:opacity-50"
                            >
                              Перегенерировать
                            </button>
                            <button
                              onClick={() => sendReplyMutation.mutate()}
                              disabled={sendReplyMutation.isPending || !replyText.trim()}
                              className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50"
                            >
                              {sendReplyMutation.isPending ? 'Отправка...' : 'Отправить в тред'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Закрыть
                </button>
                {lead.message && lead.status === 'message_generated' && (
                  <button
                    onClick={handleSend}
                    disabled={sendMessageMutation.isPending}
                    className="btn-primary"
                  >
                    {sendMessageMutation.isPending ? 'Отправка...' : 'Отправить сообщение'}
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="p-8 text-center text-gray-500">
              Лид не найден
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
